import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { PublicSection, Reveal } from '@/components/public';

/**
 * VerticalFaq — spoke FAQ block.
 *
 * Props:
 *   items  — [{ id, q, a }]        (a can be a string OR JSX with inline links)
 *   schema — [{ q, plainAnswer }]  (used to build the FAQPage JSON-LD; plain text)
 *
 * The visible answers may contain React nodes (for inline links), but
 * schema.org FAQPage `acceptedAnswer.text` must be plain strings — hence
 * the separate `schema` prop.
 */
export default function VerticalFaq({
  items,
  schema,
  testId = 'vertical-faq',
  eyebrow = 'FAQ',
  title = 'Frequently asked questions',
  description,
}) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: schema.map((s) => ({
      '@type': 'Question',
      name: s.q,
      acceptedAnswer: { '@type': 'Answer', text: s.plainAnswer },
    })),
  };

  // Escape `<` so serialized JSON cannot break out of the script tag.
  const jsonLdString = JSON.stringify(jsonLd).replace(/</g, '\\u003c');

  return (
    <PublicSection
      id="faq"
      testId={`${testId}-section`}
      variant="default"
      eyebrow={eyebrow}
      title={title}
      description={description}
    >
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdString }} />
      <Reveal>
        <div className="max-w-3xl mx-auto" data-testid={testId}>
          <Accordion type="single" collapsible className="w-full">
            {items.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                data-testid={`${testId}-item-${item.id}`}
                className="border-b border-border"
              >
                <AccordionTrigger
                  data-testid={`${testId}-trigger-${item.id}`}
                  className="text-left text-base md:text-lg font-heading font-semibold text-foreground hover:no-underline"
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`${testId}-answer-${item.id}`}
                  className="text-sm md:text-base text-muted-foreground leading-relaxed"
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Reveal>
    </PublicSection>
  );
}
